import { isDevMode } from "@/shared/lib/config";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { AppImage } from "@/shared/ui/AppImage";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import { FormDateQuarterTimeField } from "@/shared/ui/FormDateQuarterTimeField";
import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { SparklesIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import type {
  EventFeatureKey,
  EventFeaturePreset,
  EventFeaturesDTO,
} from "../../domain/contracts/event-features";
import { EventFeaturesSchema } from "../../domain/contracts/event-features";
import { validateEventFeatures } from "../../domain/services/event-features";
import { performanceApi } from "../api/client";
import { useEventCategoriesLookup } from "../api/queries";
import {
  EVENT_FEATURE_DESCRIPTIONS,
  EVENT_FEATURE_LABELS,
  EVENT_FORM_FEATURE_KEYS,
  EVENT_PRESET_LABELS,
  detectEventFeaturePreset,
  eventFeaturesFromPreset,
  eventHasFeature,
  isEventFeatureLocked,
  mergeLockedEventFeatures,
} from "../lib/event-features-ui";
import { useEventDateConflictCheck } from "../lib/use-event-date-conflict-check";
import { EventDateConflictAlert } from "./EventDateConflictAlert";

const SELECT_NONE_VALUE = "__none__";

function toSelectNoneValue(value?: string | null): string {
  return value ?? SELECT_NONE_VALUE;
}

function fromSelectNoneValue(value?: string | null): string | null {
  return value && value !== SELECT_NONE_VALUE ? value : null;
}

const EventFormSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຊື່ງານ"),
    status: z.enum(["draft", "confirmed", "completed", "cancelled"]),
    categoryId: z.string().min(1, "ຕ້ອງເລືອກຫມວດງານ"),
    featurePreset: z.enum([
      "performance_show",
      "manual_event",
      "organizer_kol_media",
      "custom",
    ]),
    eventFeatures: EventFeaturesSchema,
    eventDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    endDate: z.string().nullable().optional(),
    locationText: z.string().nullable().optional(),
    locationProvince: z.string().nullable().optional(),
    isProvincial: z.boolean(),
    perDiemPolicyId: z.string().nullable().optional(),
    perDiemDays: z.number().int().min(1).nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.isProvincial && vals.perDiemDays === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["perDiemDays"],
        message: "ຈຳນວນວັນ per diem ຕ້ອງ ≥ 1",
      });
    }
    try {
      validateEventFeatures(vals.eventFeatures);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["eventFeatures", "rehearsals"],
        message: "ວັນຊ້ອມຕ້ອງເປີດ feature ນັກສະແດງກ່ອນ",
      });
    }
  });

export type EventFormValues = z.infer<typeof EventFormSchema>;

type EventFormProps = {
  initialValues?: Partial<EventFormValues>;
  onSubmit: (values: EventFormValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
  lockedFeatures?: EventFeatureKey[];
  hideFooter?: boolean;
  /** ให้ parent เรียก submit เมื่อ hideFooter */
  registerSubmit?: (submit: () => void) => void;
  /** ใช้ใน RequestConvertPage — ลด padding/spacing รอบนอก */
  embedded?: boolean;
  /** แก้ไขงานเดิม — ไม่นับ conflict กับตัวเอง */
  excludeEventId?: string;
  /** คิวที่แปลงเป็นงานนี้ — ไม่นับ conflict กับคิวต้นทาง */
  excludeRequestId?: string;
};

function FormSection({
  title,
  description,
  children,
  embedded = false,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  embedded?: boolean;
}) {
  return (
    <section
      className={
        embedded
          ? "space-y-4 rounded-lg border bg-muted/20 p-4 sm:p-5"
          : "space-y-4 border-b pb-8 last:border-b-0 last:pb-0"
      }
    >
      <div>
        <h3 className="font-semibold text-sm">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

const PRESET_OPTIONS = (
  [
    "performance_show",
    "manual_event",
    "organizer_kol_media",
    "custom",
  ] as EventFeaturePreset[]
).map((value) => ({
  value,
  label: EVENT_PRESET_LABELS[value],
}));

function featureFieldName(key: keyof EventFeaturesDTO) {
  return `eventFeatures.${key}` as const;
}

const EMPTY_LOCKED_FEATURES: EventFeatureKey[] = [];

export function EventForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  lockedFeatures = EMPTY_LOCKED_FEATURES,
  hideFooter = false,
  registerSubmit,
  embedded = false,
  excludeEventId,
  excludeRequestId,
}: EventFormProps) {
  const defaultFeatures =
    initialValues?.eventFeatures ??
    eventFeaturesFromPreset(initialValues?.featurePreset ?? "performance_show");
  const initialPreset =
    initialValues?.featurePreset ?? detectEventFeaturePreset(defaultFeatures);

  const methods = RHF.useForm<EventFormValues>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: {
      title: initialValues?.title ?? "",
      status: initialValues?.status ?? "draft",
      categoryId: initialValues?.categoryId ?? "",
      featurePreset: initialPreset,
      eventFeatures: defaultFeatures,
      eventDate: toDateTimeLocalValue(initialValues?.eventDate) || "",
      endDate: toDateTimeLocalValue(initialValues?.endDate) || null,
      locationText: initialValues?.locationText ?? null,
      locationProvince: initialValues?.locationProvince ?? null,
      isProvincial: initialValues?.isProvincial ?? false,
      perDiemPolicyId: toSelectNoneValue(initialValues?.perDiemPolicyId),
      perDiemDays: initialValues?.perDiemDays ?? 1,
      notes: initialValues?.notes ?? null,
    },
  });

  const isProvincial = methods.watch("isProvincial");
  const featurePreset = methods.watch("featurePreset");
  const eventFeatures = methods.watch("eventFeatures");
  const categoryId = methods.watch("categoryId");
  const eventDateValue = methods.watch("eventDate");
  const endDateValue = methods.watch("endDate");

  const { conflicts: dateConflicts, isChecking: isCheckingDateConflicts } =
    useEventDateConflictCheck({
      eventDate: eventDateValue,
      endDate: endDateValue,
      excludeEventId,
      excludeRequestId,
    });

  const categoryLookup = useEventCategoriesLookup();
  const categoryOptions = useMemo(
    () =>
      (categoryLookup.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [categoryLookup.data?.items],
  );

  const selectedCategory = useMemo(
    () =>
      (categoryLookup.data?.items ?? []).find((item) => item.id === categoryId),
    [categoryId, categoryLookup.data?.items],
  );

  const perDiemLookup = useQuery({
    queryKey: ["performance", "per-diem-policies", "lookup"],
    queryFn: () => performanceApi.perDiemPolicies.lookup(),
    enabled: isProvincial,
  });
  const perDiemPolicyOptions = [
    { value: "__none__", label: "ນະໂຍບາຍມາດຕະຖານ (STANDARD_PROVINCIAL)" },
    ...(perDiemLookup.data?.items ?? []).map((p) => ({
      value: p.id,
      label: `${p.name} (${p.code})`,
    })),
  ];

  const skipPresetDetectRef = useRef(false);
  const initialFeaturesRef = useRef(initialValues?.eventFeatures ?? defaultFeatures);
  const appliedPresetRef = useRef<EventFeaturePreset>(initialPreset);

  useEffect(() => {
    if (featurePreset === "custom") {
      appliedPresetRef.current = featurePreset;
      return;
    }
    if (appliedPresetRef.current === featurePreset) return;
    appliedPresetRef.current = featurePreset;
    skipPresetDetectRef.current = true;
    const presetFeatures = eventFeaturesFromPreset(featurePreset);
    methods.setValue(
      "eventFeatures",
      mergeLockedEventFeatures(
        presetFeatures,
        initialFeaturesRef.current,
        lockedFeatures,
      ),
    );
  }, [featurePreset, lockedFeatures, methods]);

  useEffect(() => {
    if (skipPresetDetectRef.current) {
      skipPresetDetectRef.current = false;
      return;
    }
    const detected = detectEventFeaturePreset(eventFeatures);
    if (detected !== featurePreset) {
      methods.setValue("featurePreset", detected);
    }
  }, [eventFeatures, featurePreset, methods]);

  useEffect(() => {
    if (isProvincial) {
      const days = methods.getValues("perDiemDays");
      if (days == null || days < 1) {
        methods.setValue("perDiemDays", 1);
      }
    }
  }, [isProvincial, methods]);

  useEffect(() => {
    if (eventFeatures.rehearsals && !eventFeatures.performers) {
      methods.setValue("eventFeatures.rehearsals", false);
    }
  }, [eventFeatures.performers, eventFeatures.rehearsals, methods]);

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(() => {
      void methods.handleSubmit((vals) => {
        const features = mergeLockedEventFeatures(
          vals.eventFeatures,
          initialFeaturesRef.current,
          lockedFeatures,
        );
        const preset = detectEventFeaturePreset(features);

        onSubmit({
          ...vals,
          featurePreset: preset,
          eventFeatures: features,
          perDiemPolicyId: fromSelectNoneValue(vals.perDiemPolicyId),
          perDiemDays: vals.isProvincial ? (vals.perDiemDays ?? 1) : null,
        });
      })();
    });
  }, [
    lockedFeatures,
    methods,
    onSubmit,
    registerSubmit,
  ]);

  const handleAutoFill = () => {
    methods.reset({
      title: "Midsummer Symphony — Act I",
      status: "draft",
      categoryId: categoryOptions[0]?.value ?? "",
      featurePreset: "performance_show",
      eventFeatures: eventFeaturesFromPreset("performance_show"),
      eventDate: toDateTimeLocalValue(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      ),
      endDate: toDateTimeLocalValue(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 90 * 60 * 1000),
      ),
      locationText: "Main Concert Hall",
      locationProvince: null,
      isProvincial: false,
      perDiemPolicyId: "__none__",
      perDiemDays: 1,
      notes:
        "Specify staging requirements, technical riders, or VIP logistics.",
    });
  };

  const handleAutoFillOrganizer = () => {
    methods.reset({
      title: "KOL Campaign — Brand X",
      status: "draft",
      categoryId:
        categoryOptions.find((c) => c.label.toLowerCase().includes("kol"))
          ?.value ??
        categoryOptions[0]?.value ??
        "",
      featurePreset: "organizer_kol_media",
      eventFeatures: eventFeaturesFromPreset("organizer_kol_media"),
      eventDate: toDateTimeLocalValue(
        new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
      ),
      endDate: null,
      locationText: "Studio / On-site",
      locationProvince: "ວຽງຈັນ",
      isProvincial: false,
      perDiemPolicyId: "__none__",
      perDiemDays: 1,
      notes: "Organizer — AR ໃນເອກະສານ, AP ໃນ Outsource + Agent",
    });
  };

  const handleAutoFillEvent = () => {
    methods.reset({
      title: "Corporate Gala Night 2026",
      status: "draft",
      categoryId: categoryOptions.at(-1)?.value ?? categoryOptions[0]?.value ?? "",
      featurePreset: "manual_event",
      eventFeatures: eventFeaturesFromPreset("manual_event"),
      eventDate: toDateTimeLocalValue(
        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      ),
      endDate: toDateTimeLocalValue(
        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000 + 4 * 60 * 60 * 1000),
      ),
      locationText: "Convention Center Hall A",
      locationProvince: "ວຽງຈັນ",
      isProvincial: false,
      perDiemPolicyId: "__none__",
      perDiemDays: 1,
      notes: "ງານ Event — ເພີ່ມລາຍການເອງໃນໃບສະເໜີລາຄາ",
    });
  };

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        const features = mergeLockedEventFeatures(
          vals.eventFeatures,
          initialFeaturesRef.current,
          lockedFeatures,
        );
        const preset = detectEventFeaturePreset(features);

        onSubmit({
          ...vals,
          featurePreset: preset,
          eventFeatures: features,
          perDiemPolicyId: fromSelectNoneValue(vals.perDiemPolicyId),
          perDiemDays: vals.isProvincial ? (vals.perDiemDays ?? 1) : null,
        });
      }}
    >
      <div className={embedded ? "space-y-4" : "space-y-0"}>
        <FormSection title="ຂໍ້ມູນພື້ນຖານ" embedded={embedded}>
          <FormInput
            name="title"
            label="ຊື່ງານສະແດງ"
            placeholder="ຕົວຢ່າງ: Midsummer Symphony - Act I"
            requiredMark
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 font-medium text-sm">
                ຫມວດງານ <span className="text-destructive">*</span>
              </p>
              <div className="flex items-start gap-3">
                {selectedCategory?.logoStorageKey ? (
                  <div className="size-11 shrink-0 overflow-hidden rounded-lg border bg-muted">
                    <AppImage
                      src={selectedCategory.logoStorageKey}
                      alt=""
                      className="size-full"
                      fit="contain"
                    />
                  </div>
                ) : (
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground text-xs">
                    —
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <FormSelect
                    name="categoryId"
                    options={categoryOptions}
                    placeholder="ເລືອກຫມວດງານ"
                  />
                </div>
              </div>
            </div>
            <FormSelect
              name="status"
              label="ສະຖານະງານ"
              options={[
                { value: "draft", label: "ກຳລັງວາງແຜນ" },
                { value: "confirmed", label: "ຍືນຍັນແລ້ວ" },
                { value: "completed", label: "ສຳເລັດ" },
                { value: "cancelled", label: "ຍົກເລີກ" },
              ]}
            />
          </div>
        </FormSection>

        <FormSection
          title="ຕາຕະລາງ & ສະຖານທີ່"
          description="ວັນເວລາຈັດງານ ແລະ ສະຖານທີ່ປະຕິບັດ"
          embedded={embedded}
        >
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <FormDateQuarterTimeField
                name="eventDate"
                label="ວັນເວລາເລີ່ມ"
                requiredMark
              />
              <FormDateQuarterTimeField
                name="endDate"
                label="ວັນເວລາສິ້ນສຸດ / ຊ່ວງເວລາ"
                allowEmpty
              />
              <EventDateConflictAlert
                conflicts={dateConflicts}
                isChecking={isCheckingDateConflicts}
              />
            </div>
            <div className="space-y-4">
              <FormInput
                name="locationText"
                label="ສະຖານທີ່ຈັດງານ"
                placeholder="ຕົວຢ່າງ: ຫ້ອງສະແດງຫຼັກ"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  name="locationProvince"
                  label="ແຂວງ"
                  placeholder="ຕົວຢ່າງ: ຫຼວງພະບາງ"
                />
                <FormCheckboxCard
                  name="isProvincial"
                  label="ງານຕ່າງແຂວງ"
                  description="ເບີຍລ້ຽງ/ພັກ — ຄິດໃນໃບເບີກຈ່າຍ"
                  className="h-full"
                />
              </div>
              {isProvincial ? (
                <div className="grid gap-4 rounded-lg border bg-muted/20 p-3 sm:grid-cols-2">
                  <FormSelect
                    name="perDiemPolicyId"
                    label="ນະໂຍບາຍ per diem"
                    options={perDiemPolicyOptions}
                  />
                  <FormNumber
                    name="perDiemDays"
                    label="ຈຳນວນວັນ"
                    min={1}
                    placeholder="1"
                  />
                  <p className="col-span-full text-muted-foreground text-xs">
                    ເບ້ຍລ້ຽງ/ພັກຈະຖືກເພີ່ມໃນໃບເບີກຈ່າຍອັດຕະໂນມັດ (ແຍກຈາກຄ່າຈ້າງບົດບາດ)
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Preset & Features"
          description={
            lockedFeatures.length > 0
              ? "Feature ທີ່ມີຂໍ້ມູນແລ້ວ ບໍ່ສາມາດປ່ຽນໄດ້ — ປັບໄດ້ເຉພາະ feature ທີ່ຍັງວ່າງ"
              : "ເລືອກ preset ເປັນຈຸດເລີ່ມ — ປັບ feature ໄດ້ຕະຫຼອດ"
          }
          embedded={embedded}
        >
          <div className="grid gap-4 lg:grid-cols-[minmax(0,260px)_1fr]">
            <FormSelect
              name="featurePreset"
              label="Preset"
              options={PRESET_OPTIONS}
            />
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-3 font-medium text-sm">Features</p>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {EVENT_FORM_FEATURE_KEYS.map((key) => (
                  <FormCheckboxCard
                    key={key}
                    name={featureFieldName(key)}
                    label={EVENT_FEATURE_LABELS[key]}
                    description={EVENT_FEATURE_DESCRIPTIONS[key]}
                    disabled={
                      isEventFeatureLocked(key, lockedFeatures) ||
                      (key === "rehearsals" &&
                        !eventHasFeature(eventFeatures, "performers"))
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection title="ໝາຍເຫດ & ຂໍ້ກຳນົດ" embedded={embedded}>
          <FormTextarea
            name="notes"
            placeholder="ລະບຸຄວາມຕ້ອງການດ້ານເທຄນິກ..."
            rows={4}
          />
        </FormSection>
      </div>

      {!hideFooter ? (
        <div
          className={
            embedded
              ? "mt-4 flex items-center justify-between gap-4 border-t pt-4"
              : "sticky bottom-0 z-10 mt-8 flex items-center justify-between gap-4 border-t bg-background py-4"
          }
        >
          <p className="text-muted-foreground text-xs">
            ການປ່ຽນແປງຈະຖືກບັນທຶກໄວ້ໃນ Admin Console.
          </p>
          <div className="flex items-center gap-2">
            {isDevMode && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAutoFill}
                  disabled={submitting}
                >
                  <SparklesIcon className="size-4" />
                  ຕົວຢ່າງ (Show)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAutoFillOrganizer}
                  disabled={submitting}
                >
                  <SparklesIcon className="size-4" />
                  ຕົວຢ່າງ (Organizer)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAutoFillEvent}
                  disabled={submitting}
                >
                  <SparklesIcon className="size-4" />
                  ຕົວຢ່າງ (Event)
                </Button>
              </>
            )}
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={submitting}
              >
                ຍົກເລີກ
              </Button>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      ) : null}
    </FormRoot>
  );
}
